/**
 * OpenEmrShell — OpenEMR (open-source EHR) simulation.
 * Bootstrap 4 aesthetic, light blue patient banner, clinical sidebar.
 * Demonstrates how RAAPID integrates via SMART on FHIR into OpenEMR.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MARGARET, DOROTHY,
  OPENEMR_VITALS, OPENEMR_CONDITIONS, OPENEMR_MEDICATIONS, OPENEMR_LABS, OPENEMR_NOTES, OPENEMR_ORDERS, OPENEMR_CHART_DOCS,
  DOROTHY_VITALS, DOROTHY_CONDITIONS, DOROTHY_MEDICATIONS, DOROTHY_LABS, DOROTHY_NOTES, DOROTHY_ORDERS, DOROTHY_CHART_DOCS,
} from './data/openemrDummy';
import { MOCK_PROVIDERS, type MockProvider } from './data/mockProviders';

const PATIENTS = {
  'CRN-PAT-00482': {
    info: MARGARET,
    vitals: OPENEMR_VITALS, conditions: OPENEMR_CONDITIONS,
    meds: OPENEMR_MEDICATIONS, labs: OPENEMR_LABS,
    notes: OPENEMR_NOTES, orders: OPENEMR_ORDERS,
    chartDocs: OPENEMR_CHART_DOCS,
    apptTime: '9:00 AM', status: 'In Room',
  },
  'CRN-PAT-00715': {
    info: DOROTHY,
    vitals: DOROTHY_VITALS, conditions: DOROTHY_CONDITIONS,
    meds: DOROTHY_MEDICATIONS, labs: DOROTHY_LABS,
    notes: DOROTHY_NOTES, orders: DOROTHY_ORDERS,
    chartDocs: DOROTHY_CHART_DOCS,
    apptTime: '10:30 AM', status: 'Scheduled',
  },
} as const;

type PatientId = keyof typeof PATIENTS;
const TABS = ['Summary', 'Encounters', 'Issues', 'History', 'Documents', 'RAAPID Coding'];

const OE = {
  navBg: '#2E3B4E',
  navText: '#FFFFFF',
  navHover: '#3D4F65',
  navBorder: '#253344',
  accent: '#007BFF',
  accentHover: '#0056B3',
  accentLight: '#E3F2FD',
  teal: '#17A2B8',
  bannerBg: '#D1ECF1',
  bannerBorder: '#BEE5EB',
  bannerText: '#0C5460',
  allergyBg: '#F8D7DA',
  allergyText: '#721C24',
  contentBg: '#FFFFFF',
  pageBg: '#F4F4F4',
  headerBg: '#E9ECEF',
  headerText: '#495057',
  border: '#DEE2E6',
  text: '#212529',
  textMuted: '#6C757D',
  success: '#28A745',
  danger: '#DC3545',
  warning: '#FFC107',
  white: '#FFFFFF',
};

export default function OpenEmrShell() {
  const navigate = useNavigate();
  const [selectedProvider, setSelectedProvider] = useState<MockProvider | null>(null);
  const [patientId, setPatientId] = useState<PatientId>('CRN-PAT-00482');
  const [activeTab, setActiveTab] = useState('Summary');
  const [rightOpen, setRightOpen] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);

  const handleMessage = useCallback((e: MessageEvent) => {
    if (e.origin === window.location.origin && e.data?.raapid === 'openFull') setActiveTab('RAAPID Coding');
  }, []);
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const pt = PATIENTS[patientId];
  const { info } = pt;
  const providerSpecialty = selectedProvider?.specialty ?? 'PCP';
  const providerNameParam = encodeURIComponent(selectedProvider?.name ?? '');
  const compactUrl = `${window.location.origin}${import.meta.env.BASE_URL}compact?mode=mock&theme=openemr&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;
  const panelUrl = `${window.location.origin}${import.meta.env.BASE_URL}app?mode=mock&theme=openemr&patientId=${patientId}&specialty=${providerSpecialty}&providerName=${providerNameParam}`;

  if (!selectedProvider) {
    return <ProviderPicker onSelect={setSelectedProvider} onBack={() => navigate('/')} />;
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', fontSize: '14px', background: OE.pageBg, overflow: 'hidden' }}>

      {/* ── Top nav bar (Bootstrap dark navbar) ────────────────────────── */}
      <div style={{ background: OE.navBg, height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: OE.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }} title="Demo home">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: OE.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: '12px', fontWeight: 900 }}>O</span>
            </div>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>OpenEMR</span>
          </div>
          {/* Nav menu items */}
          {['Patient/Client', 'Fees', 'Modules', 'Reports'].map(item => (
            <span key={item} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
              onMouseEnter={e => e.currentTarget.style.background = OE.navHover}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{item}</span>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Patient search */}
          <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '6px', width: '200px' }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>&#x1F50D;</span>
            <input placeholder="Patient Name or ID" style={{ background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: '12px', width: '100%' }} readOnly />
          </div>
          {/* Provider dropdown */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setProfileOpen(!profileOpen)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: profileOpen ? OE.navHover : 'transparent', border: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: '4px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: OE.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '10px', fontWeight: 700 }}>{selectedProvider.initials}</div>
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px' }}>{selectedProvider.name}</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>{profileOpen ? '▲' : '▼'}</span>
            </button>
            {profileOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, background: OE.white, border: `1px solid ${OE.border}`, borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', width: '220px', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${OE.border}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: OE.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700 }}>{selectedProvider.initials}</div>
                  <div>
                    <div style={{ color: OE.text, fontSize: '13px', fontWeight: 600 }}>{selectedProvider.name}</div>
                    <div style={{ color: OE.textMuted, fontSize: '11px' }}>{selectedProvider.title}</div>
                  </div>
                </div>
                <div style={{ padding: '6px 10px', borderBottom: `1px solid ${OE.border}`, color: OE.textMuted, fontSize: '12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = OE.accentLight}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >About OpenEMR</div>
                <div style={{ padding: '6px 10px', color: OE.danger, fontSize: '12px', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  onClick={() => setSelectedProvider(null)}
                >Sign Out</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Patient banner (light blue — OpenEMR distinctive) ──────────── */}
      <div style={{ background: OE.bannerBg, border: `1px solid ${OE.bannerBorder}`, padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: OE.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
            {info.name.split(',')[0].slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: 700, fontSize: '16px', color: OE.bannerText }}>{info.name}</span>
              <span style={{ color: OE.bannerText, fontSize: '13px' }}>DOB: {info.dob} ({info.age} {info.gender?.charAt(0)})</span>
              <span style={{ color: OE.bannerText, fontSize: '13px' }}>MRN: {info.mrn}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '3px' }}>
              {/* Allergies warning */}
              <span style={{ background: OE.allergyBg, color: OE.allergyText, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px' }}>
                Allergies: Penicillin, Sulfa drugs
              </span>
              <span style={{ color: OE.textMuted, fontSize: '12px' }}>Ins: {info.plan}</span>
              <span style={{ color: OE.textMuted, fontSize: '12px' }}>Provider: {selectedProvider.name}</span>
            </div>
          </div>
        </div>
        {/* Patient switcher dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', color: OE.bannerText, fontWeight: 600, whiteSpace: 'nowrap' }}>Switch Patient:</span>
          <select
            value={patientId}
            onChange={e => setPatientId(e.target.value as PatientId)}
            style={{ fontSize: '12px', fontWeight: 600, color: OE.bannerText, background: OE.white, border: `1px solid ${OE.bannerBorder}`, borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', outline: 'none' }}
          >
            {(Object.keys(PATIENTS) as PatientId[]).map(id => (
              <option key={id} value={id}>
                {PATIENTS[id].info.name.split(',')[0]} — {PATIENTS[id].apptTime}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Chart tabs (Bootstrap nav-tabs) ────────────────────────────── */}
      <div style={{ display: 'flex', background: OE.white, borderBottom: `1px solid ${OE.border}`, flexShrink: 0, padding: '0 16px' }}>
        {TABS.map(tab => {
          const active = activeTab === tab;
          const isRaapid = tab === 'RAAPID Coding';
          return (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '10px 16px', border: 'none', borderBottom: active ? `3px solid ${isRaapid ? OE.success : OE.accent}` : '3px solid transparent',
              background: 'transparent', color: active ? (isRaapid ? OE.success : OE.accent) : OE.textMuted,
              fontWeight: active ? 700 : 400, fontSize: '13px', cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              {isRaapid && <span style={{ marginRight: '4px' }}>&#9670;</span>}
              {tab}
            </button>
          );
        })}
      </div>

      {/* ── Main layout (sidebar + content + compact panel) ────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Clinical sidebar (left — OpenEMR distinctive feature) */}
        <div style={{ width: '240px', flexShrink: 0, background: OE.white, borderRight: `1px solid ${OE.border}`, overflowY: 'auto' }}>
          <SidebarSection title="Active Problems">
            {pt.conditions.slice(0, 6).map(c => (
              <div key={c.icd} style={{ padding: '4px 0', borderBottom: `1px solid ${OE.pageBg}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '11px', color: OE.text, lineHeight: 1.3 }}>{c.display}</span>
                <code style={{ fontSize: '9px', color: OE.textMuted, background: OE.headerBg, padding: '1px 4px', borderRadius: '3px', marginLeft: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>{c.icd}</code>
              </div>
            ))}
          </SidebarSection>
          <SidebarSection title="Medications">
            {pt.meds.slice(0, 5).map((m, i) => (
              <div key={i} style={{ padding: '3px 0', borderBottom: `1px solid ${OE.pageBg}` }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: OE.text }}>{'name' in m ? m.name : ''}</div>
                <div style={{ fontSize: '10px', color: OE.textMuted }}>{'sig' in m ? m.sig : ''}</div>
              </div>
            ))}
          </SidebarSection>
          <SidebarSection title="Allergies">
            {['Penicillin (rash)', 'Sulfa drugs (anaphylaxis)'].map(a => (
              <div key={a} style={{ padding: '3px 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ color: OE.danger, fontSize: '10px' }}>&#x26A0;</span>
                <span style={{ fontSize: '11px', color: OE.allergyText }}>{a}</span>
              </div>
            ))}
          </SidebarSection>
          <SidebarSection title="Latest Vitals">
            <VR label="BP" value={pt.vitals[0].bp} alert={parseInt(pt.vitals[0].bp) > 140} />
            <VR label="HR" value={pt.vitals[0].hr} />
            <VR label="SpO2" value={pt.vitals[0].spo2} alert={parseInt(pt.vitals[0].spo2) < 95} />
            <VR label="BMI" value={pt.vitals[0].bmi} alert={parseFloat(pt.vitals[0].bmi) > 30} />
            <VR label="Temp" value={pt.vitals[0].temp} />
          </SidebarSection>
        </div>

        {/* Center content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} key={patientId}>
          {activeTab === 'RAAPID Coding' ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ background: '#D4EDDA', borderBottom: `1px solid #C3E6CB`, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <span style={{ background: OE.success, color: 'white', fontSize: '9px', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>SMART on FHIR</span>
              </div>
              <iframe src={panelUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%' }} title="RAAPID Coding" allow="clipboard-write" />
            </div>
          ) : activeTab === 'Summary' ? (
            <SummaryTab pt={pt} />
          ) : activeTab === 'Issues' ? (
            <IssuesTab conditions={pt.conditions} />
          ) : activeTab === 'Encounters' ? (
            <EncountersTab info={pt.info} />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: OE.textMuted }}>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>{activeTab}</div>
              <div style={{ fontSize: '12px' }}>Content area for {activeTab} tab</div>
            </div>
          )}
        </div>

        {/* Right compact RAAPID panel */}
        <div style={{ width: rightOpen ? '360px' : '36px', flexShrink: 0, borderLeft: `1px solid ${OE.border}`, display: 'flex', flexDirection: 'column', background: OE.white, transition: 'width 0.2s ease', overflow: 'hidden' }}>
          <div style={{ background: OE.success, padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            {rightOpen && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <img src={`${import.meta.env.BASE_URL}raapid-logo.png`} alt="RAAPID" style={{ height: '14px', filter: 'brightness(0) invert(1)' }} />
                <span style={{ color: 'white', fontSize: '11px', fontWeight: 600, opacity: 0.9 }}>Coding Intelligence</span>
              </div>
            )}
            <button onClick={() => setRightOpen(!rightOpen)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '12px', marginLeft: rightOpen ? 0 : 'auto' }}>{rightOpen ? '→' : '←'}</button>
          </div>
          {rightOpen && <iframe key={patientId} src={compactUrl} style={{ flex: 1, height: 0, minHeight: 0, border: 'none', width: '100%', background: OE.pageBg }} title="RAAPID Compact" allow="clipboard-write" />}
        </div>
      </div>

      {/* Status bar */}
      <div style={{ background: OE.headerBg, borderTop: `1px solid ${OE.border}`, padding: '3px 16px', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ color: OE.textMuted, fontSize: '10px' }}>OpenEMR 8.0.0 · ONC Certified · SMART on FHIR v2.2.0</span>
        <span style={{ color: OE.textMuted, fontSize: '10px' }}>{selectedProvider.name} · {selectedProvider.title} · {new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );
}

// ─── Tab content ────────────────────────────────────────────────────────────

function SummaryTab({ pt }: { pt: typeof PATIENTS[PatientId] }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignContent: 'start' }}>
      <Card title="Demographics" sub={pt.info.mrn}>
        <VR label="Name" value={pt.info.name} />
        <VR label="DOB / Age" value={`${pt.info.dob} (${pt.info.age})`} />
        <VR label="Sex" value={pt.info.gender || 'Female'} />
        <VR label="Insurance" value={pt.info.plan} />
        <VR label="Physician" value={pt.info.physician} />
      </Card>
      <Card title="Latest Vitals" sub={pt.vitals[0].date}>
        <VR label="Blood Pressure" value={pt.vitals[0].bp} alert={parseInt(pt.vitals[0].bp) > 140} />
        <VR label="Heart Rate" value={pt.vitals[0].hr} />
        <VR label="SpO2" value={pt.vitals[0].spo2} alert={parseInt(pt.vitals[0].spo2) < 95} />
        <VR label="Weight / BMI" value={`${pt.vitals[0].wt} / BMI ${pt.vitals[0].bmi}`} alert={parseFloat(pt.vitals[0].bmi) > 30} />
        <VR label="Temp / Resp" value={`${pt.vitals[0].temp} / ${pt.vitals[0].rr}`} />
      </Card>
      <Card title="Medical Problems" sub={`${pt.conditions.length} active`}>
        {pt.conditions.slice(0, 6).map(c => (
          <div key={c.icd} style={{ padding: '4px 0', borderBottom: `1px solid ${OE.pageBg}`, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', color: OE.text }}>{c.display}</span>
            <code style={{ fontSize: '10px', color: OE.textMuted, background: OE.headerBg, padding: '1px 4px', borderRadius: '3px', marginLeft: '6px', flexShrink: 0 }}>{c.icd}</code>
          </div>
        ))}
      </Card>
      <Card title="Medications" sub={`${pt.meds.length} active`}>
        {pt.meds.slice(0, 5).map((m, i) => (
          <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${OE.pageBg}` }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: OE.text }}>{'name' in m ? m.name : ''}</div>
            <div style={{ fontSize: '11px', color: OE.textMuted }}>{'sig' in m ? m.sig : ''}</div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function IssuesTab({ conditions }: { conditions: typeof OPENEMR_CONDITIONS }) {
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: OE.text, margin: 0 }}>Medical Problems</h3>
        <button style={{ background: OE.accent, color: 'white', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>+ Add</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: OE.headerBg }}>
            {['Title', 'ICD-10', 'Begin Date', 'Status'].map(h => (
              <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: OE.headerText, borderBottom: `2px solid ${OE.border}`, fontSize: '11px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {conditions.map((c, i) => (
            <tr key={c.icd} style={{ background: i % 2 ? '#F8F9FA' : 'white', borderBottom: `1px solid ${OE.pageBg}` }}>
              <td style={{ padding: '7px 10px', color: OE.text, fontWeight: 500 }}>{c.display}</td>
              <td style={{ padding: '7px 10px' }}><code style={{ fontSize: '11px', color: OE.accent, background: OE.accentLight, padding: '1px 6px', borderRadius: '3px' }}>{c.icd}</code></td>
              <td style={{ padding: '7px 10px', color: OE.textMuted, fontFamily: 'monospace', fontSize: '11px' }}>{c.onset || '2022-01-15'}</td>
              <td style={{ padding: '7px 10px' }}><span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: '#D4EDDA', color: OE.success }}>Active</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EncountersTab({ info }: { info: typeof MARGARET }) {
  const encounters = [
    { date: info.date, provider: info.physician, facility: 'Main Clinic', reason: info.encounter, status: 'Open' },
    { date: '03/10/2026', provider: info.physician, facility: 'Main Clinic', reason: 'Annual Physical', status: 'Closed' },
    { date: '01/15/2026', provider: 'Dr. Raj Patel', facility: 'Cardiology', reason: 'Cardiology Consult', status: 'Closed' },
    { date: '11/20/2025', provider: info.physician, facility: 'Main Clinic', reason: 'Follow-up Visit', status: 'Closed' },
  ];
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 700, color: OE.text, margin: 0 }}>Encounters</h3>
        <button style={{ background: OE.success, color: 'white', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>+ New Encounter</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: OE.headerBg }}>
            {['Date', 'Provider', 'Facility', 'Reason', 'Status'].map(h => (
              <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: OE.headerText, borderBottom: `2px solid ${OE.border}`, fontSize: '11px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {encounters.map((enc, i) => (
            <tr key={i} style={{ background: i % 2 ? '#F8F9FA' : 'white', borderBottom: `1px solid ${OE.pageBg}`, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = OE.accentLight}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#F8F9FA' : 'white'}
            >
              <td style={{ padding: '7px 10px', color: OE.accent, fontWeight: 600, fontFamily: 'monospace', fontSize: '11px' }}>{enc.date}</td>
              <td style={{ padding: '7px 10px', color: OE.text }}>{enc.provider}</td>
              <td style={{ padding: '7px 10px', color: OE.textMuted }}>{enc.facility}</td>
              <td style={{ padding: '7px 10px', color: OE.text }}>{enc.reason}</td>
              <td style={{ padding: '7px 10px' }}>
                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 700, background: enc.status === 'Open' ? OE.accentLight : OE.headerBg, color: enc.status === 'Open' ? OE.accent : OE.textMuted }}>{enc.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sidebar section ────────────────────────────────────────────────────────

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderBottom: `1px solid ${OE.border}` }}>
      <div style={{ background: OE.headerBg, padding: '6px 12px', fontWeight: 700, fontSize: '11px', color: OE.headerText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </div>
      <div style={{ padding: '6px 12px' }}>{children}</div>
    </div>
  );
}

// ─── Shared primitives ──────────────────────────────────────────────────────

function Card({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div style={{ background: OE.white, border: `1px solid ${OE.border}`, borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{ background: OE.headerBg, padding: '8px 12px', borderBottom: `1px solid ${OE.border}`, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: OE.headerText }}>{title}</span>
        <span style={{ fontSize: '10px', color: OE.textMuted }}>{sub}</span>
      </div>
      <div style={{ padding: '8px 12px' }}>{children}</div>
    </div>
  );
}

function VR({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${OE.pageBg}` }}>
      <span style={{ fontSize: '11px', color: OE.textMuted }}>{label}</span>
      <span style={{ fontSize: '12px', fontWeight: 600, color: alert ? OE.warning : OE.text }}>{value}</span>
    </div>
  );
}

// ─── Provider picker ────────────────────────────────────────────────────────

function ProviderPicker({ onSelect, onBack }: { onSelect: (p: MockProvider) => void; onBack: () => void }) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: OE.pageBg }}>
      <div style={{ background: OE.navBg, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: OE.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Back to home">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </button>
        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: OE.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'white', fontSize: '12px', fontWeight: 900 }}>O</span>
        </div>
        <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>OpenEMR</span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>v8.0.0</span>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: OE.success, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span style={{ color: 'white', fontSize: '24px', fontWeight: 900 }}>O</span>
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: OE.text, margin: '0 0 6px' }}>Select Provider</h2>
            <p style={{ fontSize: '13px', color: OE.textMuted, margin: 0 }}>Choose your provider profile to begin this session</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {MOCK_PROVIDERS.map(p => (
              <button key={p.initials} onClick={() => onSelect(p)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: OE.white, border: `1px solid ${OE.border}`, borderRadius: '4px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = OE.accent; e.currentTarget.style.boxShadow = `0 0 0 3px ${OE.accent}20`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = OE.border; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: OE.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>{p.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: OE.text }}>{p.name}</div>
                  <div style={{ fontSize: '12px', color: OE.textMuted, marginTop: '2px' }}>{p.title}</div>
                </div>
                <span style={{ color: OE.border, fontSize: '18px' }}>{'→'}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
